import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { array, arrayOf, bool, func, number, object, oneOfType, shape, string } from 'prop-types';
import { areAllFacetsChecked, getQueryParams } from '../../../lib/facetHelpers';
import { surveyFacetSearchActions } from '../../../reducers/modules/surveys/surveyFacetSearch/surveyFacetSearch';
import { Tooltip } from 'ps-design';
import FacetPanel from 'ps-components/FacetPanel/FacetPanel';
import InPlaceIndicator from 'ps-components/LoadingIndicator/InPlaceIndicator/InPlaceIndicator';
import Pagination from 'ps-components/Pagination/Pagination';
import Placeholder from 'ps-components/Placeholder/Placeholder';
import ReactTooltip from 'react-tooltip';
import SubHeaderSearch from './SubHeaderSearch';
import SurveyJobRow from './SurveyJobRow';
import Table from 'ps-components/Table/Table/Table';
import TableBody from 'ps-components/Table/TableBody/TableBody';
import TableHeader from 'ps-components/Table/TableHeader/TableHeader';
import './ResultDetails.scss';

const ENTER_KEY = 13;
const STEP = 50;
const COLUMNS = ['**', 'Survey Jobs', 'Survey'];

class ResultDetail extends Component {
  static propTypes = {
    actions: shape({
      facetItemClick: func,
      facetReset: func,
      pageList: func,
      search: func
    }),
    createJobMatch: func,
    deleteJobMatch: func,
    facetSelection: shape({
      pub_survey_publisher: object,
      survey_code: object,
      survey_year: object
    }),
    facets: arrayOf(
      shape({
        field: string,
        id: string,
        items: arrayOf(
          shape({
            doc_count: number,
            key: oneOfType([string, number]),
            partialDocCount: number
          })
        ),
        title: string,
        type: string
      })
    ),
    isSelected: bool,
    items: arrayOf(
      shape({
        code: string,
        description: string,
        publisher: string,
        survey: shape({
          code: string,
          name: string
        }),
        title: string,
        years: number
      })
    ),
    lastFacetFilteredID: string,
    lastQueryParams: shape({
      es_query: string,
      exclude: object,
      include: object,
      limit: number,
      offset: number,
      pageName: string
    }),
    loaded: bool,
    loading: bool,
    name: string,
    offset: number,
    replacementTokens: arrayOf(
      shape({
        token: string,
        value: string
      })
    ),
    searchResultsCount: number,
    searchText: string,
    selectedJob: shape({
      code: string,
      codeKey: string,
      currency: string,
      description: string,
      descriptionDocument: shape({
        fileLocation: string,
        title: string
      }),
      employeeCount: oneOfType([string, number]),
      id: oneOfType([string, number]),
      jobMatches: array,
      name: string
    }),
    selectedJobConnection: array,
    setSearchText: func,
    showText: bool,
    showZeros: bool,
    toggleShowSelectedSurveyJobs: func,
    useJobCodeKey: bool
  };

  componentDidMount() {
    const { actions, name, selectedJob } = this.props;
    const params = getQueryParams(name);

    if (selectedJob) {
      params.include.currency = [selectedJob.currency];
    }

    actions.search({ params });
  }

  componentDidUpdate(nextProps) {
    const { selectedJob } = this.props;

    if (
      (selectedJob.currency === nextProps.selectedJob.currency && this.props.loaded !== nextProps.loaded) ||
      selectedJob.currency !== nextProps.selectedJob.currency
    ) {
      this.handleReset();
    }
  }

  componentWillUnmount() {
    this.handleReset();
  }

  clearSearch = () => {
    const { actions, setSearchText } = this.props;

    actions.searchInputSearch('');
    setSearchText('');
  };

  generateTooltip = () => {
    const { description, name } = this.props.selectedJob;

    return <Tooltip display="underline" hoverText={description} id={name} position="bottom" tooltipText={name} />;
  };

  getJobsToRender = () => {
    const { isSelected, items, selectedJobConnection } = this.props;
    return isSelected && items && items.length
      ? items
      : !isSelected
      ? selectedJobConnection.map(job => {
          return { isMeritMatch: job.isMeritMatch, ...job.surveyJob };
        })
      : [];
  };

  handleReset = () => {
    const { actions, name, selectedJob, setSearchText } = this.props;
    const params = getQueryParams(name);

    actions.search({
      params: { ...params, currency: selectedJob.currency, include: { currency: [selectedJob.currency] } },
      clear: true
    });

    setSearchText('');
  };

  pageSurveyJobs = (first, newAfter) => {
    const { actions } = this.props;

    actions.pageList(first, newAfter);
  };

  renderPlaceholder = () => {
    const { isSelected } = this.props;
    const secondaryText = isSelected
      ? 'There are no results for your search.'
      : 'You have not selected any matches for this job.';
    return (
      <Placeholder imagePath="/view/static/mp/images/empty_results.svg" showText={true} secondaryText={secondaryText} />
    );
  };

  renderSurveyJobRows(jobsToRender) {
    const { createJobMatch, deleteJobMatch, selectedJob, selectedJobConnection } = this.props;

    return jobsToRender.map((job, index) => (
      <SurveyJobRow
        createJobMatch={createJobMatch}
        deleteJobMatch={deleteJobMatch}
        key={index}
        surveyJob={job}
        selectedJob={selectedJob}
        selectedJobConnection={selectedJobConnection}
      />
    ));
  }

  renderTable = () => {
    const { isSelected, items, loaded, loading, offset, searchResultsCount } = this.props;
    const shouldShowMore = !!items.length && items.length % STEP === 0 && items.length !== searchResultsCount;
    const jobsToRender = this.getJobsToRender();
    const surveyJobRows = this.renderSurveyJobRows(jobsToRender);

    if (surveyJobRows.length > 0) {
      return (
        <div className="survey__jobs-table">
          <ReactTooltip id="merit-match-indicator" effect="solid">
            <span>Match from Market Pricing</span>
          </ReactTooltip>
          {!loading && loaded ? (
            <Table className="details__table" hasStickyColumns={true}>
              <TableHeader
                cellClass="details__table-header-cell"
                columns={COLUMNS}
                headerClass="details__table-header"
                rowClass="details__table-header-row"
              />
              <TableBody className="details__table-body">{surveyJobRows}</TableBody>
            </Table>
          ) : (
            <InPlaceIndicator />
          )}
          {isSelected && (
            <Pagination
              className="surveyJobs__pagination"
              isSelected={isSelected}
              pageAction={this.pageSurveyJobs}
              shouldShowInput={true}
              shouldShowMore={shouldShowMore}
              shouldShowRanges={true}
              step={STEP}
              offset={offset}
              totalCount={searchResultsCount}
            />
          )}
        </div>
      );
    } else {
      this.renderPlaceholder();
    }
  };

  searchByText = e => {
    const { actions, isSelected, searchText } = this.props;

    if (e.keyCode === ENTER_KEY && isSelected) {
      actions.searchInputSearch(searchText);
    }
  };

  render() {
    const {
      actions,
      facets,
      facetSelection,
      isSelected,
      lastFacetFilteredID,
      lastQueryParams,
      loaded,
      replacementTokens,
      searchResultsCount,
      searchText,
      selectedJob,
      selectedJobConnection,
      setSearchText,
      showZeros,
      toggleShowSelectedSurveyJobs,
      useJobCodeKey
    } = this.props;

    const pdfPath =
      selectedJob.descriptionDocument && selectedJob.descriptionDocument.fileLocation
        ? selectedJob.descriptionDocument.fileLocation.substring(
            17,
            selectedJob.descriptionDocument.fileLocation.length
          )
        : null;
    const hasJobDescription = selectedJob.description;
    const orgJobCodeKey = replacementTokens.find(value => value.token === 'job_code_key_text');
    const orgJobCodeKeyLabel = orgJobCodeKey && orgJobCodeKey.value ? orgJobCodeKey.value : 'Job Code Key';
    const showResetButton =
      areAllFacetsChecked(facets, facetSelection) || (lastQueryParams && lastQueryParams.es_query !== '');

    return (
      <div className="details">
        <div className="details__header">
          <h2 className="details__title">{selectedJob.title}</h2>
          <div className="details__name">{hasJobDescription ? this.generateTooltip() : selectedJob.name}</div>
          <div className="details__data">
            <div className="details__section">
              Job Code
              <span className="details__data-item">{selectedJob.code}</span>
            </div>
            {useJobCodeKey && (
              <div className="details__section">
                {orgJobCodeKeyLabel}
                <span className="details__data-item">{selectedJob.codeKey}</span>
              </div>
            )}
            <div className="details__section">
              Currency
              <span className="details__data-item">{selectedJob.currency}</span>
            </div>
            <div className="details__section">
              Total EE
              <span className="details__data-item">{selectedJob.employeeCount}</span>
            </div>
          </div>
          <div className="details__link-container">
            {selectedJob.descriptionDocument && selectedJob.descriptionDocument.fileLocation && (
              <a
                className="details__link"
                href={`/view/static/mp/documents${pdfPath}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="icon-doc-text" />
                Job description
              </a>
            )}
          </div>
        </div>
        <div className="details__body">
          <FacetPanel
            className="survey__job-facets"
            facetItemClick={actions.facetItemClick}
            facetReset={actions.facetReset}
            facets={facets}
            facetSelection={facetSelection}
            hiddenFacets={['job_title']}
            isDisabled={!isSelected}
            isLoaded={loaded}
            lastFacetFilteredID={lastFacetFilteredID}
            limit={5}
            resetSearch={this.handleReset}
            showFacetSectionSearch={true}
            showResetButton={showResetButton}
            showZeros={showZeros}
          />
          <div className="details__results">
            <div className="subheader">
              <SubHeaderSearch
                clearSearch={this.clearSearch}
                isSelected={isSelected}
                placeholderText="Search survey jobs"
                searchId="survey__jobs-search-job-matching"
                searchByText={this.searchByText}
                searchText={searchText}
                selectedItems={selectedJobConnection}
                setSearchText={setSearchText}
                toggleSelected={toggleShowSelectedSurveyJobs}
                totalCount={searchResultsCount}
              />
              {this.getJobsToRender().length > 0 && (
                <div className="subheader__note pxl-text-xsmall">Sorted by Relevancy</div>
              )}
            </div>
            {this.renderTable()}
          </div>
        </div>
      </div>
    );
  }
}
const mapStateToProps = ({ surveyFacetSearch }) => ({
  facets: surveyFacetSearch.facets,
  facetSelection: surveyFacetSearch.facetSelection,
  items: surveyFacetSearch.items,
  lastFacetFilteredID: surveyFacetSearch.lastFacetFilteredID,
  lastQueryParams: surveyFacetSearch.lastQueryParams,
  loaded: surveyFacetSearch.loaded,
  loading: surveyFacetSearch.loading,
  name: surveyFacetSearch.name,
  offset: surveyFacetSearch.offset,
  searchResultsCount: surveyFacetSearch.searchResultsCount,
  showZeros: surveyFacetSearch.showZeros
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      facetItemClick: surveyFacetSearchActions.facetItemClick,
      facetReset: surveyFacetSearchActions.facetReset,
      pageList: surveyFacetSearchActions.pageList,
      search: surveyFacetSearchActions.search,
      searchInputSearch: surveyFacetSearchActions.searchInputSearch
    },
    dispatch
  )
});

export default connect(mapStateToProps, mapDispatchToProps)(ResultDetail);
